/** @file This file deals with manipulating paths as strings. */
/**
 * Splits a given path string into an array of non-empty segments.
 *
 * The function:
 * 1. Splits the input string using the slash (`/`) character as the delimiter.
 * 2. Filters out any empty segments caused by consecutive slashes or leading/trailing slashes.
 *
 * @param path The input path string to split.
 * @returns An array of non-empty path segments.
 *
 * @example
 * splitPath('/foo/bar/baz') // Returns ['foo', 'bar', 'baz']
 *
 * @example
 * splitPath('///foo//bar///baz/') // Returns ['foo', 'bar', 'baz']
 *
 * @example
 * splitPath('/') // Returns []
 *
 * @example
 * splitPath('no/slashes') // Returns ['no', 'slashes']
 */
export function splitPath(path) {
    return path.split('/').filter(x => x);
}
/**
 * Returns the directory portion of a given file path.
 *
 * The function:
 * 1. Removes any trailing slash (`/`) from the input path.
 * 2. Finds the last occurrence of a slash (`/`) to identify the directory portion.
 * 3. If no slash is found, throws an error indicating that the directory cannot be determined.
 *
 * It throws if the input path does not contain a valid directory portion (no `/`).
 *
 * @param path The input path string.
 * @returns The directory portion of the path, excluding the file name or trailing slash.
 *
 * @example
 * dirName('/foo/bar/baz') // Returns '/foo/bar'
 *
 * @example
 * dirName('/foo/bar/baz/') // Returns '/foo/bar'
 *
 * @example
 * dirName('file') // Throws Error: Getting directory failed on path: file
 */
export function dirName(path) {
    let start = path.length - 1;
    while (start >= 0 && path[start] === '/') {
        start -= 1;
    }
    if (start >= 0) {
        const idx = path.lastIndexOf('/', start);
        if (idx >= 0) {
            return path.slice(0, idx);
        }
    }
    throw Error(`Getting directory failed on path: ${path}`);
}
/**
 * Normalizes slashes in a given string by.
 *
 * It does this by:
 * 1. Replacing multiple consecutive slashes (`//`, `///`, etc.) with a single slash (`/`).
 * 2. Removing trailing slashes (`/`) at the end of the string.
 *
 * @param inp The input string containing slashes to normalize.
 * @returns The normalized string with no consecutive slashes and no trailing slash.
 *
 * @example
 * normalizeSlashes('///foo//bar///baz/') // Returns 'foo/bar/baz'
 *
 * @example
 * normalizeSlashes('/leading/slash/') // Returns '/leading/slash'
 *
 * @example
 * normalizeSlashes('///') // Returns ''
 *
 * @example
 * normalizeSlashes('no/slashes') // Returns 'no/slashes'
 */
export function normalizeSlashes(inp) {
    return inp.replace(/\/\/+/g, '/').replace(/\/+$/g, '');
}
/**
 * Makes the path adheres to the following.
 *  - has exactly one leading slash.
 *  - has no trailing slash.
 *  - has no duplicate slashes.
 *
 * @param path Any string like "foo/bar", "/foo//bar/", etc.
 * @returns The absolute paths.
 */
export function normalizePathWithLeadingSlash(path) {
    const norm = normalizeSlashes(path);
    return norm.startsWith('/') ? norm : `/${norm}`;
}
/**
 * This checks a given path for relative segment, which is defined as the
 * any segment in a path which contains only the current or the parent directory.
 *
 * @param path Is path to check.
 * @returns A true if there are relative segments.
 */
export function hasRelativeSegments(path) {
    for (const segment of splitPath(path)) {
        if (segment === '.' || segment === '..') {
            return true;
        }
    }
    return false;
}
/**
 * Returns whether the given path is a directory or not.
 *
 * @param path Is path to check.
 * @returns A true if path ends in a directory.
 */
export function isDirectoryPath(path) {
    return path.endsWith('/');
}
